import { RealtimeServiceClient } from '@taxfyle/api-internal/internal/realtime_grpc_web_pb'
import {
  RealtimeEvent,
  StreamEventOptions,
} from '@taxfyle/api-internal/internal/realtime_pb'
import { Empty } from 'google-protobuf/google/protobuf/empty_pb'
import { createGrpcAuthenticator } from 'infra/grpc-authenticator'
import { defer, merge, Observable, of } from 'rxjs'
import {
  delay,
  first,
  map,
  retry,
  share,
  startWith,
  switchMap,
} from 'rxjs/operators'
import { APIConfig } from 'types/definitions'
import { enableDevTools, fromStreamingCall } from 'utils/grpc-util'

/**
 * Creates an observable that emits realtime events from That New Work API.
 * The observable is shared so only a single connection is opened.
 *
 * @param config
 * @constructor
 */
export function WorkRealtime(config: APIConfig) {
  return defer(() => {
    const client = new RealtimeServiceClient(config.baseURL)
    const grpcAuthenticator = createGrpcAuthenticator(config)
    enableDevTools(client)
    return grpcAuthenticator.meta$.pipe(
      // We don't want to reconnect anytime the token refreshes since
      // we only need to verify the token once at connection time.
      first(),
      switchMap((meta) =>
        fromStreamingCall(() =>
          client.streamEvents(new StreamEventOptions(), meta),
        ),
      ),
      // Reconnect after 40 seconds of no activity
      realtimeRetryPolicy(40_000),
    )
  }).pipe(share())
}

/**
 * Realtime stream policy which
 * 1) Reconnects after a short delay if there are any errors.
 * 2) Reconnects if no messages are received within a specific grace period (server
 * is expected to send a ping every so often).
 * @param pingGracePeriodMs Amount of time to wait for a ping before reconnecting
 */
function realtimeRetryPolicy<T>(pingGracePeriodMs: number) {
  return (source$: Observable<T>) =>
    source$.pipe(
      // emit a ping right away otherwise we don't start the
      // pipe which emits an error if we dont receive a message
      startWith(new RealtimeEvent().setPing(new Empty())),
      switchMap((value) =>
        merge(
          of(value),
          // throw an error after timeout if nothing is received
          of(1).pipe(
            delay(pingGracePeriodMs),
            map(() => {
              throw new Error(`No new thing after ${pingGracePeriodMs}ms.`)
            }),
          ),
        ),
      ),
      // log and retry after some delay
      retry({ delay: 1000 }),
    )
}
